import UserAccount from './UserAccount.mjs';

export const tokenTiers = [{
  label: 'Token',
  min: 400000,
  max: 699999
}, {
  label: 'Token',
  min: 700000,
  max: 999999
}, {
  label: 'Token',
  min: 1000000,
  max: 1499999
}];
export const tokenStep = 500000;

export const primeTokensEarnedOnTier = {
  Escape: 1,
  Delight: 2,
  Fantasy: 3,
  illusionist: 1,
  wizard: 2,
  oracle: 3,
};

// The following object was provided by Wind Creek. Be careful changing this.
export const REWARD_TYPE = {
  BONUS_POINTS: 'A',
  BONUS_POINTS_CATEGORY: 'Bonus Points',
  FOOD_CREDIT: 'F',
  FREE_PLAY: 'P',
  FREE_PLAY_PRIZE_CATEGORY: 'G',
  SHOPPING_DOLLARS: 'shopping-dollars',
  SHOPPING_DOLLARS_CATEGORY: 'Shopping Dollars',
  HOTEL_STAY: 'R',
  HOTEL_STAY_PRIZE_CATEGORY: 'H',
  WSTORE_EARLY_ACCESS: 'WStoreEarlyAccess',
  DRAWING_TICKET: 'DrawingTicket',
  DRAWING_TICKET_PRIZE_CATEGORY: 'D',
  SPA: 'S',
  SOFT_OFFER_CMP: 'CMP',
  WSCORE: 'WScore',
  WSCORE_PRIZE_CATEGORY: 'W',
  OF_NO_ACTION: 'OF - No Action',
  BATTLE_SLOTS: 'Battle Slots',
  BATTLE_SLOTS_PRIZE_CATEGORY: 'B',
  QUEST: 'Q',
  COUPON: 'U',
  CASINO_TICKET: 'K',
  DEFAULT: 'default'
};

export function getCurTierFromPoints(points) {
  const tierPoints = WindCreekData.rewardTiers;
  let tier = tierPoints.find((tier) => points >= tier.min && points <= tier.max);
  if(!tier) {
    tier = tierPoints[tierPoints.length - 1];
  }
  return tier;
}

export function getNextTierFromPoints(points) {
  const tierPoints = WindCreekData.rewardTiers;
  const curTier = getCurTierFromPoints(points);
  if(curTier === tierPoints[tierPoints.length - 1]) {
    return "Unknown";
  }
  return tierPoints[tierPoints.indexOf(curTier) + 1];
}

export function getNextEarnedPrimeTokenAmount(profileData) {
  let nextEarnedPrimeTokenAmount;
  if (UserAccount.isOnHighestTier(profileData)) {
    // On the highest tier, 3 tokens are always earned when reaching the next point threshold
    nextEarnedPrimeTokenAmount = 3;
  } else {
    // This user is not on the highest tier, so get how many tokens are earned when they get upgraded to the next tier
    nextEarnedPrimeTokenAmount = primeTokensEarnedOnTier[getNextTierFromPoints(profileData.basePointsEarned).label];
  }

  return nextEarnedPrimeTokenAmount;
}

export function findPointsToNextToken(curPoints) {
  //Fantasy, but not yet into token tiers
  if(curPoints < tokenTiers[0].min) {
    return tokenTiers[0].min - curPoints;
  } else {
    const range = tokenTiers.find((tier) => curPoints >= tier.min && curPoints <= tier.max);
    if(range) {
      //In preset token tiers
      return range.max - curPoints + 1;
    } else {
      // beyond tiers
      let base = tokenTiers[tokenTiers.length - 1].max + 1;
      const diff = Math.floor((curPoints - base) / tokenStep);
      base = diff > 0 ? diff * tokenStep + base : base;
      const next = base + tokenStep;
      return next - curPoints;
    }
  }
}

export class Rewards {
  // Converts dates from the WindCreek API to a format that the Date constructor can understand
  static getDate(reward) {
    let formattedStartDate, formattedEndDate, redeemedDate;
    if (reward.isPartnerReward) {
      formattedStartDate = reward.startDateUTC.replaceAll('/', '-').replace(' ', 'T') + 'Z';
      formattedEndDate = reward.endDateUTC.replaceAll('/', '-').replace(' ', 'T') + 'Z';
      redeemedDate = null; // Partner reward redemption works differently so this date is handled elsewhere
    } else {
      if (UserAccount.isMiamiSite()) {
        formattedStartDate = reward.validFromDateTimeUTC  + 'Z';
        formattedEndDate = reward.validToDateTimeUTC + 'Z';
        redeemedDate = null;
      } else {
        formattedStartDate = reward.startDateUTC.replaceAll('/', '-').replace(' ', 'T') + 'Z';
        formattedEndDate = reward.endDateUTC.replaceAll('/', '-').replace(' ', 'T') + 'Z';
        if (reward?.redeemedDateUTC) {
          redeemedDate = reward.redeemedDateUTC.replaceAll('/', '-').replace(' ', 'T') + 'Z';
          redeemedDate = new Date(redeemedDate);
        }
      }
    }
    return {
      startDate: new Date(formattedStartDate),
      endDate: new Date(formattedEndDate),
      redeemedDate: redeemedDate
    };
  }

  // Returns an object with the rewards grouped by their status, either available or past (past = expired or redeemed)
  static groupByStatus(rewards, partnerRewards) {
    let currentDate = new Date();
    let availableRewards = [];
    let pastRewards = [];

    rewards.forEach(reward => {
      reward.isPartnerReward = false;

      if (UserAccount.isMiamiSite() && reward.assignmentState === 'voided') {
        // voided PLS rewards should not be displayed anywhere in the UI, so skip this one and go to the next reward
        return;
      }

      if (UserAccount.isMiamiSite()) {
        if (reward.assignmentState === 'available' && reward.isExpired === false) {
          availableRewards.push(reward);
        } else {
          pastRewards.push(reward);
        }
      } else {
        let endDate = this.getDate(reward).endDate;
        if (currentDate >= endDate || reward.redeemed >= reward.maxPrizesPerPeriod) {
          pastRewards.push(reward);
        } else {
          availableRewards.push(reward);
        }
      }
    });

    partnerRewards.forEach(reward => {
      reward.isPartnerReward = true;

      let currentPartnerRewardStates = [
        'voucher_program_created',
        'voucher_program_code_claimed',
      ];

      let pastPartnerRewardsStates = [
        'voucher_program_code_redeemed',
        'voucher_program_canceled',
        'voucher_program_expired',
      ];

      if (currentPartnerRewardStates.includes(reward.event.eventType)) {
        availableRewards.push(reward);
      } else if (pastPartnerRewardsStates.includes(reward.event.eventType)) {
        pastRewards.push(reward);
      } else {
        /*
        TODO:
        If this is an unrecognized state, don't add the reward to either array
        so it won't be displayed anywhere in the UI. The documentation mentions
        other states and it's not clear what to do in those situations, but
        either way it doesn't sound like they're safe for displaying to users.
         */
      }
    });

    return {
      available: availableRewards,
      past: pastRewards
    };
  }

  // The following function was provided by Wind Creek. Be careful changing this.
  static getType(reward) {
    if (UserAccount.isMiamiSite()) {
      return REWARD_TYPE.DEFAULT;
    }

    if (
      reward.isSoftOffer &&
      reward.prizeType === REWARD_TYPE.SOFT_OFFER_CMP &&
      reward.prize &&
      reward.prize.category
    ) {
      let prizeType = reward.prize.category.charAt(0).toString();
      if (prizeType === REWARD_TYPE.FREE_PLAY_PRIZE_CATEGORY) {
        return REWARD_TYPE.FREE_PLAY;
      } else if (prizeType === REWARD_TYPE.HOTEL_STAY_PRIZE_CATEGORY) {
        return REWARD_TYPE.HOTEL_STAY;
      } else if (prizeType === REWARD_TYPE.DRAWING_TICKET_PRIZE_CATEGORY) {
        return REWARD_TYPE.DRAWING_TICKET;
      } else if (prizeType === REWARD_TYPE.WSCORE_PRIZE_CATEGORY) {
        return REWARD_TYPE.WSCORE;
      } else if (
        reward.prize.category === REWARD_TYPE.SHOPPING_DOLLARS_CATEGORY
      ) {
        return REWARD_TYPE.SHOPPING_DOLLARS;
      } else if (reward.prize.category === REWARD_TYPE.BONUS_POINTS_CATEGORY) {
        return REWARD_TYPE.BONUS_POINTS;
      } else {
        return prizeType;
      }
    } else {
      return reward.prizeType;
    }
  }

  // The following function was provided by Wind Creek. Be careful changing this.
  static getTitle(type) {
    const rewardTitleMap = {
      [REWARD_TYPE.BONUS_POINTS]: 'Bonus Points',
      [REWARD_TYPE.FOOD_CREDIT]: 'Food Credit',
      [REWARD_TYPE.FREE_PLAY]: 'Free Play',
      [REWARD_TYPE.SHOPPING_DOLLARS]: 'Shopping Dollars',
      [REWARD_TYPE.SPA]: 'Spa',
      [REWARD_TYPE.HOTEL_STAY]: 'Hotel Stay',
      [REWARD_TYPE.WSTORE_EARLY_ACCESS]: 'WStore Early Access',
      [REWARD_TYPE.DRAWING_TICKET]: 'Drawing Ticket',
      [REWARD_TYPE.DRAWING_TICKET_PRIZE_CATEGORY]: 'Drawing Ticket',
      [REWARD_TYPE.WSCORE]: 'WScore',
      [REWARD_TYPE.OF_NO_ACTION]: 'OF - No Action',
      [REWARD_TYPE.BATTLE_SLOTS]: 'Battle Spins',
      [REWARD_TYPE.BATTLE_SLOTS_PRIZE_CATEGORY]: 'Battle Spins',
      [REWARD_TYPE.QUEST]: 'Quest',
      [REWARD_TYPE.COUPON]: 'Coupon',
      [REWARD_TYPE.CASINO_TICKET]: 'Casino Ticket',
      [REWARD_TYPE.DEFAULT]: 'Reward'
    };

    return rewardTitleMap[type] || rewardTitleMap[REWARD_TYPE.DEFAULT];
  }
}
